/* eslint-disable no-console */
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useCallback, useEffect, useRef } from 'react';

import { useWakeLock } from './useWakeLock';

import { apiClient } from '@/api/axios';
import { apiScopes } from '@/auth/msal';
import signalRService from '@/services/signalRService';
import { decodeJWT } from '@/utils/auth';

const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes in milliseconds
const CHECK_INTERVAL = 60 * 1000; // 1 minute in milliseconds

export const useTokenRefresh = () => {
  const { instance, accounts } = useMsal();
  const refreshIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const lastTokenCheckRef = useRef<number>(Date.now());
  const { requestWakeLock } = useWakeLock();

  const displayFreshTokenExpiration = useCallback((token: string) => {
    const decodedToken = decodeJWT(token);
    if (!decodedToken?.exp) {
      console.error('Invalid token: missing expiration');
      return;
    }
    console.info(
      'Token refreshed, valid until: ' +
        new Date(decodedToken.exp * 1000).toLocaleString()
    );
  }, []);

  /**
   * Extract the token from the Authorization header
   * @param header - The Authorization header
   * @returns The token
   */
  const extractTokenFromAuthHeader = useCallback(
    (header: string): string | null => {
      if (!header) {
        return null;
      }
      const parts = header.split(' ');
      return parts.length === 2 && parts[1] ? parts[1] : null;
    },
    []
  );

  const refreshToken = useCallback(async () => {
    try {
      if (!accounts.length) {
        console.warn('No accounts available for token refresh');
        return;
      }

      // Try silent token acquisition first
      try {
        const result = await instance.acquireTokenSilent({
          scopes: apiScopes,
          account: accounts[0],
        });

        if (result?.accessToken) {
          apiClient.defaults.headers.Authorization = `Bearer ${result.accessToken}`;
          displayFreshTokenExpiration(result.accessToken);
          instance.setActiveAccount(result.account);
          await signalRService.reconnectWithNewAuthHeader(
            `Bearer ${result.accessToken}`
          );
          await signalRService.startConnection(`Bearer ${result.accessToken}`);
          return;
        }
      } catch (error) {
        console.warn('Silent token acquisition failed:', error);

        // Special handling for InteractionRequiredAuthError
        if (error instanceof InteractionRequiredAuthError) {
          // Always try popup first, fallback to redirect if needed
          try {
            console.info('Attempting interactive token acquisition via popup');
            const popupResult = await instance.acquireTokenPopup({
              scopes: apiScopes,
            });

            if (popupResult?.accessToken) {
              apiClient.defaults.headers.Authorization = `Bearer ${popupResult.accessToken}`;
              displayFreshTokenExpiration(popupResult.accessToken);
              instance.setActiveAccount(popupResult.account);
              await signalRService.reconnectWithNewAuthHeader(
                `Bearer ${popupResult.accessToken}`
              );
              return;
            }
          } catch (popupError) {
            // Popup failed, try redirect as a fallback
            console.error('Popup token acquisition failed:', popupError);
            console.info('Fallback to redirect authentication');
            try {
              await instance.acquireTokenRedirect({
                scopes: apiScopes,
                redirectUri: window.location.origin,
              });
            } catch (redirectError) {
              console.error(
                'Failed to initiate redirect for token refresh:',
                redirectError
              );
            }
          }
        } else {
          console.error('Unexpected error during token refresh:', error);
        }
      }
    } catch (error) {
      console.error('Token refresh failed with unexpected error:', error);
    }
  }, [accounts, instance, displayFreshTokenExpiration]);

  const checkAndRefreshToken = useCallback(async () => {
    const currentToken = extractTokenFromAuthHeader(
      apiClient.defaults.headers.Authorization as string
    );

    if (!currentToken) {
      if (accounts.length) {
        await refreshToken();
      }
      return;
    }

    const decodedToken = decodeJWT(currentToken);
    if (!decodedToken?.exp) {
      console.error('Invalid token: missing expiration');
      return;
    }

    const expiryTime = decodedToken.exp * 1000;
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    if (timeUntilExpiry <= TOKEN_REFRESH_THRESHOLD) {
      await refreshToken();
    }
  }, [accounts, extractTokenFromAuthHeader, refreshToken]);

  const startTokenRefreshInterval = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    refreshIntervalRef.current = setInterval(
      checkAndRefreshToken,
      CHECK_INTERVAL
    );
  }, [checkAndRefreshToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        // When tab becomes visible, check token immediately if it's been a while
        if (now - lastTokenCheckRef.current > CHECK_INTERVAL) {
          checkAndRefreshToken();
          lastTokenCheckRef.current = now;
        }
        requestWakeLock();
      }
    };

    const currentToken = extractTokenFromAuthHeader(
      apiClient.defaults.headers.Authorization as string
    );

    if (currentToken || accounts.length) {
      startTokenRefreshInterval();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [
    accounts,
    checkAndRefreshToken,
    extractTokenFromAuthHeader,
    requestWakeLock,
    startTokenRefreshInterval,
  ]);
};
